import Image from '@components/atoms/Image'
import { ProductOption, ProductVariant, Product } from '@interfaces/product'
import { setPDPVariant } from '@slices/PDPSlice'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import SimilarVariants from './SimilarVariants'

interface VariantSelectorProps {
  currentVariant: ProductVariant | undefined
  option: ProductOption
  setCurrentVariant: React.Dispatch<React.SetStateAction<ProductVariant | undefined>>
  variants: ProductVariant[]
  product: Product
}

const VariantSelector: React.FC<VariantSelectorProps> = ({ currentVariant, option, setCurrentVariant, variants, product }) => {
  const [filteredValues, setFilteredValues] = useState<string[]>(option.values.map((v) => v.toString()))
  const dispatch = useDispatch()
  const [isSimilarVariantsDrawerOpen, setIsSimilarVariantsDrawerOpen] = useState<boolean>(false);

  const handleCloseSimilarVariantsDrawer: () => void = () => setIsSimilarVariantsDrawerOpen(false)
  const handleVariantSelect = (value: string) => {
    if (!currentVariant) return

    const updatedOptions = currentVariant.selectedOptions.map((o) =>
      o.name === option.name ? { name: o.name, value } : o,
    )

    const newVariant = variants.find(
      (variant) => JSON.stringify(variant.selectedOptions) === JSON.stringify(updatedOptions),
    )

    setCurrentVariant(newVariant)
  }

  useEffect(() => {
    if (currentVariant) {
      dispatch(setPDPVariant(currentVariant))
      const regularPriceSpan = document.querySelector('div.body-price span.regular-price')
      const salePriceSpan = document.querySelector('div.body-price span.sale-price')
      if (currentVariant.salePrice) {
        if (salePriceSpan) {
          salePriceSpan.innerHTML = currentVariant.salePrice || ''
        }
        if (regularPriceSpan) {
          regularPriceSpan.innerHTML = currentVariant.price.money || ''
          regularPriceSpan.classList.add('text-[#767676]', 'line-through')
        }
      } else if (currentVariant.price.money ) {
        if (regularPriceSpan) {
          regularPriceSpan.innerHTML = currentVariant.price.money || ''
          regularPriceSpan.classList.remove('text-[#767676]', 'line-through')
        }
        if (salePriceSpan) {
          salePriceSpan.innerHTML = ''
        }
      }
    }
  }, [currentVariant, dispatch])

  useEffect(() => {
    if (!currentVariant) return

    const selectedOptions = currentVariant.selectedOptions

    const availableValues = variants
      .filter(
        (variant) =>
          JSON.stringify(variant.selectedOptions.filter((o) => o.name !== option.name)) ===
          JSON.stringify(selectedOptions.filter((o) => o.name !== option.name)),
      )
      .map((variant) => variant.selectedOptions.find((o) => o.name === option.name)?.value)
      .filter((value): value is string => value !== undefined)

    setFilteredValues([...new Set(availableValues)])
  }, [currentVariant, variants, option.name])

  const isLensType = option.name.toLowerCase() === 'lens type'
  const isSize = option.name.toLowerCase() === 'size'
  const hasSizeOption = product.options?.some(opt =>
    opt.name.toLowerCase() === 'size'
  );

  // Keep this in case we want to hide single lens variant thumbnails - MH 1/21/26
  // if (filteredValues.length < 2) {
  //  return null
  // }

  return (
    <div className="pb-2">
      <div className="flex items-center justify-between mb-2">
        <label className="block mb-2 text-sm font-medium">{option.name}</label>
        {isSize ? (
          <a
            href="#size-guide-drawer-trigger"
            data-size-guide-drawer-trigger
            className="text-sm font-medium underline text-black hover:no-underline"
          >
            {window.DEPT.Strings.product.sizeGuide}
          </a>
        ) : isLensType && !hasSizeOption && (
          <a
            href="#size-guide-drawer-trigger"
            data-size-guide-drawer-trigger
            className="text-sm font-medium underline text-black hover:no-underline"
            >
            {window.DEPT.Strings.product.sizeGuide}
          </a>
          )}
      </div>

      <div className={`flex gap-[10px] ${isLensType ? 'flex-wrap sm:flex-row' : 'flex-wrap'} lg:gap-4`}>
        {filteredValues.map((value) => {
          const isSelected = currentVariant?.selectedOptions.some((o) => o.name === option.name && o.value === value)

          const associatedVariant = variants.find(
            (variant) =>
              JSON.stringify(variant.selectedOptions) ===
              JSON.stringify(
                currentVariant?.selectedOptions.map((o) => (o.name === option.name ? { name: o.name, value } : o)),
              ),
          )

          const isOutOfStock = !associatedVariant?.availableForSale

          const buttonClasses = isLensType
            ? [
                'flex',
                'flex-col',
                'items-center',
                'justify-center',
                'p-2',
                'border',
                'rounded-lg',
                'w-[105px]',
                'h-[105px]',
                'shadow-thumbnail',
                isOutOfStock ? 'opacity-50' : 'hover:border-red',
                isSelected ? 'border-black' : '',
              ]
                .filter(Boolean)
                .join(' ')
            : [
                'grid',
                'grid-rows-[auto,1fr]',
                'items-center',
                'justify-center',
                'p-2',
                'border',
                'rounded-lg',
                'min-w-[52px]',
                'max-w-[105px]',
                'shadow-thumbnail',
                isOutOfStock ? 'opacity-50' : 'hover:border-red',
                isSelected ? 'bg-gradient-primary-metalic text-white' : 'bg-gradient-model-grey text-black',
              ]
                .filter(Boolean)
                .join(' ')

          return (
            <button
              key={`${option.name}-${value}`}
              type="button"
              className={`${buttonClasses} relative`}
              onClick={() => handleVariantSelect(value)}
            >
              {isOutOfStock && (
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                  <div
                    className={`absolute top-1/2 left-0 h-[2px] bg-grey-4 transform ${
                      isLensType ? 'rotate-[135deg] w-[135%] -ml-[17%]' : 'w-full rotate-[159deg]'
                    }`}
                  ></div>
                </div>
              )}

              <div className="flex flex-col items-center justify-center">
                {isLensType && associatedVariant?.image?.src ? (
                  <Image
                    alt={associatedVariant.image.alt || ''}
                    src={associatedVariant.image.src}
                    srcWidths={{ sm: 100 }}
                    height={50}
                    width={50}
                    classes="object-contain"
                    blendMode="multiply"
                  />
                ) : null}
                <div className={`flex justify-end flex-col text-center py-1 w-full ${isLensType ? 'min-h-11' : ''}`}>
                  <span className="text-sm font-medium whitespace-normal line-clamp-2">{value}</span>
                </div>
              </div>
            </button>
          )
        })}
        {isLensType && (
          <>
            <button
              key={`${product.mainCategory?.handle}`}
              type="button"
              className={`flex flex-col items-center justify-center p-2 border rounded-lg w-[105px] h-[105px] shadow-thumbnail hover:border-red relative`}
              onClick={() => setIsSimilarVariantsDrawerOpen(true)}
            >
              <div className="flex flex-col items-center justify-center">
                <Image
                  alt={product.modelStyle || ''}
                  src={product.modelStyle || ''}
                  srcWidths={{ sm: 100 }}
                  height={60}
                  width={60}
                  classes="object-contain"
                  blendMode="multiply"
                />
                <div className={`flex justify-end flex-col text-center py-1 w-full min-h-11`}>
                  <span className="text-sm font-medium whitespace-normal line-clamp-2">{window.DEPT.Strings.product.viewSimilarVariants}</span>
                </div>
              </div>
            </button>
            <SimilarVariants product={product} isOpen={isSimilarVariantsDrawerOpen}
                             onClose={handleCloseSimilarVariantsDrawer} />
          </>
        )}
      </div>
    </div>
  )
}

export default VariantSelector
